import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserQRCodeReader } from "@zxing/browser";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, CameraOff, RefreshCw } from "lucide-react";

const errMsg = (e) => {
  const name = e?.name || "";
  if (name === "NotAllowedError" || name === "SecurityError") return "Consenti l'accesso alla fotocamera nelle impostazioni del browser.";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "Nessuna fotocamera disponibile.";
  if (name === "NotReadableError" || name === "TrackStartError") return "La fotocamera è già utilizzata da un'altra app.";
  return `Impossibile avviare la fotocamera (${name || "errore sconosciuto"}).`;
};

export default function QrScanner({ open, onClose, onResult }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const controlsRef = useRef(null);
  const activeRef = useRef(false);
  const [status, setStatus] = useState("idle"); // idle | starting | running | error
  const [error, setError] = useState("");

  const stopAll = useCallback(() => {
    activeRef.current = false;
    try { controlsRef.current?.stop(); } catch { /* noop */ }
    controlsRef.current = null;
    const s = streamRef.current;
    if (s) { try { s.getTracks().forEach((t) => t.stop()); } catch { /* noop */ } }
    streamRef.current = null;
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch { /* noop */ } }
  }, []);

  const getStream = useCallback(async () => {
    // 1) prova camera posteriore
    try {
      return await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
    } catch (e) {
      // 2) fallback: prima videocamera disponibile
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cam = devices.find((d) => d.kind === "videoinput");
        if (!cam) throw e;
        return await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: cam.deviceId } }, audio: false });
      } catch (e2) {
        throw e2.name === "NotFoundError" ? e2 : (e.name ? e : e2);
      }
    }
  }, []);

  const start = useCallback(async () => {
    stopAll();
    setError("");
    setStatus("starting");
    activeRef.current = true;
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Fotocamera non supportata o pagina non sicura (richiede HTTPS).");
      setStatus("error");
      return;
    }
    let timeout = setTimeout(() => {
      if (activeRef.current && status !== "running") {
        setError("La fotocamera non si è avviata in tempo. Riprova.");
        setStatus("error");
        stopAll();
      }
    }, 9000);
    try {
      const stream = await getStream();
      if (!activeRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      video.muted = true;
      await new Promise((res) => {
        if (video.readyState >= 1) return res();
        video.onloadedmetadata = () => res();
      });
      await video.play();
      clearTimeout(timeout);
      timeout = null;
      if (!activeRef.current) return;
      setStatus("running");
      const reader = new BrowserQRCodeReader();
      controlsRef.current = await reader.decodeFromVideoElement(video, (result) => {
        if (result && activeRef.current) {
          activeRef.current = false;
          onResult(result.getText());
          stopAll();
        }
      });
    } catch (e) {
      if (timeout) clearTimeout(timeout);
      setError(errMsg(e));
      setStatus("error");
      stopAll();
    }
  }, [getStream, onResult, stopAll, status]);

  useEffect(() => {
    if (open) start();
    return () => stopAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="qr-scanner-dialog">
        <DialogHeader><DialogTitle>Scansiona QR cliente</DialogTitle></DialogHeader>
        <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
          <video ref={videoRef} className="w-full h-full object-cover" data-testid="qr-scanner-video" autoPlay muted playsInline />
          {status === "starting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/90 gap-2" data-testid="qr-scanner-loading">
              <Loader2 className="w-8 h-8 animate-spin" /><span className="text-sm">Avvio fotocamera…</span>
            </div>
          )}
          {status === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 gap-3 bg-black/80" data-testid="qr-scanner-error">
              <CameraOff className="w-8 h-8 text-white/80" />
              <span className="text-sm text-white/90">{error}</span>
              <Button onClick={start} className="rounded-full" data-testid="qr-scanner-retry"><RefreshCw className="w-4 h-4 mr-2" /> RIPROVA FOTOCAMERA</Button>
            </div>
          )}
          {status === "running" && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-2/3 aspect-square border-2 border-white/70 rounded-2xl" />
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Inquadra il QR personale del cliente (pagina "Vedi il mio credito"). Nessun dato personale viaggia nel QR.</p>
      </DialogContent>
    </Dialog>
  );
}
